package dev.lingvo.site.components.editor

import androidx.compose.runtime.Composable
import androidx.compose.runtime.CompositionLocalProvider
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.core.rememberPageContext
import dev.lingvo.shared.ReviewStatus
import dev.lingvo.site.ds.rememberToasts
import dev.lingvo.site.state.ProjectRouteContext
import kotlinx.coroutines.launch

@Composable
fun TranslationEditorScreen(
    projectSlug: String,
    locale: String,
    initialKeyId: String? = null,
    initialSearch: String = "",
    initialReview: ReviewStatus? = null,
    initialStale: Boolean = false,
    /**
     * The `?state=` clause a counter linked here with — `untranslated`, `translated`, `approved`,
     * or anything else, which reads as "all".
     *
     * Every figure in the product that says "38 untranslated" now opens the editor on exactly
     * those, rather than on all 412 with the reader left to find the filter. A `String` rather than
     * the enum because this function is the module's one public entry point and the filter is
     * internal — the parse belongs on this side of that boundary anyway, since the page is handing
     * over a URL parameter, not a decision.
     */
    initialState: String? = null,
) {
    val scope = rememberCoroutineScope()
    val router = rememberPageContext().router
    val toasts = rememberToasts()
    val initialBootstrap = ProjectRouteContext.current(projectSlug)
        ?.takeIf { route -> route.project.slug == projectSlug }
        ?.editorBootstrap()
    lateinit var conflictHandler: () -> Unit
    lateinit var completionHandler: (String) -> Unit
    val state = remember(
        projectSlug,
        locale,
        initialKeyId,
        initialSearch,
        initialReview,
        initialStale,
        initialState,
        initialBootstrap,
    ) {
        val linkedState = EditorStateFilter.fromLink(initialState)
        TranslationEditorState(
            projectSlug = projectSlug,
            locale = locale,
            initialKeyId = initialKeyId,
            initialSearch = initialSearch,
            initialReview = initialReview,
            initialStale = initialStale,
            scope = scope,
            toasts = toasts,
            onConflictDetected = { conflictHandler() },
            onCompletion = { message -> completionHandler(message) },
            onNavigate = { path -> router.tryRoutingTo(path) },
            initialBootstrap = initialBootstrap,
        ).also { it.stateFilter = linkedState }
    }
    var controller by remember(state) { mutableStateOf(EditorControllerState(domain = state)) }
    val capturedFocus = remember(state) { EditorCapturedFocusRegistry() }
    val activationOrigin = remember(state) { EditorActivationOriginTracker() }
    val domBridge = remember(state) { BrowserEditorDomBridge() }
    val measurementController = remember(state) { EditorMeasurementController() }
    var compactWorkspaceClientWidth by remember(state) { mutableStateOf(0.0) }
    EditorActivationOriginEffect(activationOrigin)

    fun emitEvidence(event: EditorEvidenceEvent) {
        runCatching { emitEditorEvidence(event) }
    }

    completionHandler = { message ->
        emitEvidence(EditorEvidenceEvents.announcement(message))
        controller = announceEditorMessage(controller, message)
    }

    fun applyControllerTransition(name: String, transition: EditorTransition) {
        val before = controller.workspace
        if (
            transition.state.measuredLayoutDisposition == EditorMeasuredLayoutDisposition.MEASURING &&
            transition.state.layoutMeasurementId != before.layoutMeasurementId
        ) {
            measurementController.invalidate()
            emitEvidence(
                EditorEvidenceEvents.layoutMeasurementInvalidated(
                    previousId = before.layoutMeasurementId,
                    nextId = transition.state.layoutMeasurementId,
                ),
            )
        } else if (
            transition.state.measuredLayoutDisposition == EditorMeasuredLayoutDisposition.NOT_APPLICABLE &&
            before.measuredLayoutDisposition != EditorMeasuredLayoutDisposition.NOT_APPLICABLE
        ) {
            measurementController.invalidate()
        }
        controller = applyEditorControllerTransition(controller, transition)
        emitEvidence(EditorEvidenceEvents.transition(name, before, transition))
    }

    fun applyLatestControllerTransition(
        name: String,
        transition: (EditorWorkspaceState) -> EditorTransition,
    ) {
        var before: EditorWorkspaceState? = null
        var resolved: EditorTransition? = null
        controller = reduceEditorControllerTransition(controller) { latestWorkspace ->
            before = latestWorkspace
            transition(latestWorkspace).also { latestTransition ->
                resolved = latestTransition
            }
        }
        val beforeWorkspace = requireNotNull(before)
        val resolvedTransition = requireNotNull(resolved)
        if (
            resolvedTransition.state.measuredLayoutDisposition == EditorMeasuredLayoutDisposition.MEASURING &&
            resolvedTransition.state.layoutMeasurementId != beforeWorkspace.layoutMeasurementId
        ) {
            measurementController.invalidate()
            emitEvidence(
                EditorEvidenceEvents.layoutMeasurementInvalidated(
                    previousId = beforeWorkspace.layoutMeasurementId,
                    nextId = resolvedTransition.state.layoutMeasurementId,
                ),
            )
        } else if (
            resolvedTransition.state.measuredLayoutDisposition ==
            EditorMeasuredLayoutDisposition.NOT_APPLICABLE &&
            beforeWorkspace.measuredLayoutDisposition != EditorMeasuredLayoutDisposition.NOT_APPLICABLE
        ) {
            measurementController.invalidate()
        }
        emitEvidence(EditorEvidenceEvents.transition(name, beforeWorkspace, resolvedTransition))
    }

    fun recordQueueScroll(topPx: Double) {
        val normalized = topPx.coerceAtLeast(0.0)
        controller = controller.copy(
            workspace = controller.workspace.recordQueueScroll(normalized),
        )
        emitEvidence(
            EditorEvidenceEvents.scroll(
                revision = controller.workspace.queueQueryRevision,
                requestedTopPx = normalized,
                actualTopPx = normalized,
                effectId = null,
            ),
        )
    }

    fun sampleQueueScroll() {
        readEditorQueueScrollTop()?.let(::recordQueueScroll)
    }

    fun onSelectKey(keyId: String) {
        if (
            controller.workspace.mountsQueueList() &&
            controller.workspace.renderedLayout() == EditorRenderedLayout.COMPACT
        ) {
            sampleQueueScroll()
        }
        state.selectKey(keyId)
        if (state.selectedKeyId == keyId) {
            activeEditorTarget(state)?.let { target ->
                applyControllerTransition("select-key", controller.workspace.showEditor(target))
            }
        }
    }

    fun onShowQueue(focus: EditorFocusRef?) {
        if (!controller.workspace.mountsQueueList()) sampleQueueScroll()
        applyControllerTransition("show-queue", controller.workspace.showQueue(focus))
    }

    fun onOpenFilters(invoker: EditorFocusRef) {
        if (controller.workspace.mountsQueueList()) sampleQueueScroll()
        applyControllerTransition("open-filters", controller.workspace.openFilters(invoker))
    }

    fun onCloseFilters() {
        applyControllerTransition("close-filters", controller.workspace.closeFilters())
    }

    fun onOpenUtility(surface: EditorUtilitySurface, invoker: EditorFocusRef) {
        if (controller.workspace.mountsQueueList()) sampleQueueScroll()
        val actionOrigin = activationOrigin.consume()
        applyLatestControllerTransition("open-utility-${surface.name.lowercase()}") { latest ->
            latest.openUtility(
                surface = surface,
                invoker = invoker,
                actionOrigin = actionOrigin,
            )
        }
    }

    fun onCloseUtility(actionOrigin: EditorUtilityActionOrigin) {
        if (controller.workspace.mountsQueueList()) sampleQueueScroll()
        applyLatestControllerTransition("close-utility") { latest ->
            latest.closeUtility(
                activeTarget = activeEditorTarget(state),
                actionOrigin = actionOrigin,
            )
        }
    }

    fun onSwitchUtility(
        surface: EditorUtilitySurface,
        actionOrigin: EditorUtilityActionOrigin,
    ) {
        applyLatestControllerTransition("switch-utility-${surface.name.lowercase()}") { latest ->
            latest.switchUtility(surface, actionOrigin)
        }
    }

    fun onAdvanceUtilityPresence(generation: Long) {
        applyLatestControllerTransition("advance-utility-presence") { latest ->
            latest.advanceUtilityPresence(generation)
        }
    }

    fun onCompleteUtilityExit(generation: Long) {
        applyLatestControllerTransition("complete-utility-exit") { latest ->
            latest.completeUtilityExit(generation)
        }
    }

    fun onOpenModal(surface: EditorModalSurface, invoker: EditorFocusRef) {
        applyControllerTransition("open-modal-${surface.name.lowercase()}", controller.workspace.openModal(surface, invoker))
    }

    fun onCloseModal() {
        applyControllerTransition("close-modal", controller.workspace.closeModal())
    }

    fun onOpenOverflow(invoker: EditorFocusRef) {
        applyControllerTransition("open-overflow", controller.workspace.openOverflow(invoker))
    }

    fun onCloseOverflow() {
        applyControllerTransition("close-overflow", controller.workspace.closeOverflow())
    }

    fun onCloseTopSurface() {
        val selectedRow = state.selectedKeyId?.let(EditorFocusRef::QueueRow)
        val utilityActionOrigin = activationOrigin.consume()
        applyLatestControllerTransition("close-top-surface") { latest ->
            latest.closeTopSurface(
                selectedRow,
                activeEditorTarget(state),
                utilityActionOrigin = utilityActionOrigin,
            )
        }
    }

    fun copySourceThroughController() {
        val text = state.sourceCopyText() ?: return
        scope.launch {
            val copied = domBridge.copyText(text)
            state.toasts.show(
                if (copied) "Source copied" else "Couldn't copy the source. Select it and copy manually.",
                if (copied) dev.lingvo.site.ds.LToastVariant.SUCCESS else
                    dev.lingvo.site.ds.LToastVariant.WARNING,
            )
        }
    }

    fun insertTextThroughController(text: String) {
        val target = activeEditorTarget(state) ?: return
        val restore = state.insertAtSelection(text, domBridge.readTargetSelection(target)) ?: return
        domBridge.queueTargetSelectionRestore(restore)
        applyControllerTransition("insert-at-selection", controller.workspace.requestFocus(target))
    }

    fun insertSuggestionThroughController(
        insertion: EditorSuggestionInsertion,
        actionOrigin: EditorUtilityActionOrigin,
    ) {
        val target = EditorFocusRef.Target(
            insertion.keyId,
            insertion.pluralCategory,
            locale = state.locale,
        )
        applyLatestControllerTransition("suggestion-inserted") { latest ->
            when {
                latest.utility != EditorUtilitySurface.NONE &&
                    latest.renderedLayout() == EditorRenderedLayout.COMPACT ->
                    latest.closeUtilityForInsertion(target, actionOrigin)

                else -> latest.requestFocus(target)
            }
        }
        emitEvidence(EditorEvidenceEvents.announcement("Suggestion inserted"))
        controller = announceEditorMessage(controller, "Suggestion inserted")
    }

    conflictHandler = {
        applyControllerTransition(
            "enter-conflict",
            controller.workspace.enterConflict(EditorFocusRef.ElementId(EDITOR_CONFLICT_TARGET_ID)),
        )
    }

    EditorControllerRuntime(
        state = state,
        controller = controller,
        capturedFocus = capturedFocus,
        domBridge = domBridge,
        measurementController = measurementController,
        applyControllerTransition = ::applyControllerTransition,
        updateController = { update -> controller = update(controller) },
        emitEvidence = ::emitEvidence,
        sampleQueueScroll = ::sampleQueueScroll,
        onCompactWorkspaceWidth = { compactWorkspaceClientWidth = it },
    )
    EditorDraftProtection(state.hasUnsavedDrafts)

    DisposableEffect(state) {
        onDispose {
            capturedFocus.clear()
            domBridge.clear()
        }
    }

    // Put the caret in the new target after an advancing save. `activeEditorTarget` recomputes the
    // ref from the current key *and* plural category, which is what makes this correct across the
    // singular/plural boundary — the case where the field's element id changes and the browser
    // would otherwise drop focus on `<body>`. See `TranslationEditorState.advanceFocusRequest`.
    LaunchedEffect(state.advanceFocusRequest) {
        if (state.advanceFocusRequest == 0L) return@LaunchedEffect
        activeEditorTarget(state)?.let { target ->
            applyControllerTransition(
                "advance-focus",
                controller.workspace.requestFocus(target),
            )
        }
    }

    EditorKeyboardShortcuts(
        state = state,
        workspace = controller.workspace,
        // ↑/↓ move the selection *and* the focus ring, because they are the same thing to the
        // person pressing them. Moving only the selection left DOM focus behind on the previous
        // row: two rows drawn with the accent ring, and nothing announced to a screen reader.
        onMoveSelection = { delta ->
            state.moveSelection(delta)
            state.selectedKeyId?.let { keyId ->
                editorQueueSelectionFocusTarget(controller.workspace, keyId)?.let { target ->
                    applyControllerTransition(
                        "queue-selection-focus",
                        controller.workspace.requestFocus(target),
                    )
                }
            }
        },
        onCloseTopSurface = ::onCloseTopSurface,
        onFocusTarget = {
            activeEditorTarget(state)?.let { target ->
                applyControllerTransition("focus-target", controller.workspace.requestFocus(target))
            }
        },
        onFocusSearch = { onShowQueue(EditorFocusRef.Search) },
        // Alt+H fires while the caret is in the target field, so the invoker is captured from
        // wherever the caret actually is rather than assumed to be a trigger button: Escape has to
        // put the person back in the sentence they were half-way through typing.
        onOpenHistory = {
            onOpenUtility(
                EditorUtilitySurface.ACTIVITY,
                captureShortcutInvoker(capturedFocus),
            )
        },
        onOpenShortcutHelp = {
            onOpenModal(
                EditorModalSurface.SHORTCUTS,
                captureShortcutInvoker(capturedFocus),
            )
        },
        onCopySource = ::copySourceThroughController,
        onInsertText = ::insertTextThroughController,
        onInsertSuggestion = { suggestion ->
            state.insertSuggestion(suggestion)?.let { insertion ->
                insertSuggestionThroughController(insertion, activationOrigin.consume())
            }
        },
        onTab = { event ->
            trapEditorFocus(
                event = event,
                workspace = controller.workspace,
                capturedFocus = capturedFocus,
                onRequestFocus = { target, fallback ->
                    applyLatestControllerTransition("focus-trap") { latest ->
                        latest.requestFocus(target, fallback)
                    }
                },
            )
        },
    )

    // The QA blocker's "Show" reaches the caret from `EditorActionBar`, five composables down.
    // Providing the screen's own bridge here keeps production on exactly one instance without
    // adding a parameter that `EditorScreenContent`, `EditorComposition` and `TranslationPane`
    // would each carry and none would use — see `LocalEditorDomBridge`.
    CompositionLocalProvider(LocalEditorDomBridge provides domBridge) {
        EditorScreenContent(
            state = state,
            controller = controller,
            compactWorkspaceClientWidth = compactWorkspaceClientWidth,
            applyControllerTransition = ::applyControllerTransition,
            onRetryFoundation = { scope.launch { state.loadFoundation() } },
            onShowQueue = ::onShowQueue,
            onOpenFilters = ::onOpenFilters,
            onCloseFilters = ::onCloseFilters,
            onOpenUtility = ::onOpenUtility,
            onCloseUtility = ::onCloseUtility,
            onSwitchUtility = ::onSwitchUtility,
            onAdvanceUtilityPresence = ::onAdvanceUtilityPresence,
            onCompleteUtilityExit = ::onCompleteUtilityExit,
            onOpenModal = ::onOpenModal,
            onCloseModal = ::onCloseModal,
            onOpenOverflow = ::onOpenOverflow,
            onCloseOverflow = ::onCloseOverflow,
            onCopySource = ::copySourceThroughController,
            onInsertText = ::insertTextThroughController,
            onInsertSuggestion = { insertion, actionOrigin ->
                insertSuggestionThroughController(insertion, actionOrigin)
            },
            currentActivationOrigin = activationOrigin::consume,
            discardActivationOrigin = activationOrigin::discard,
            onRecordQueueScroll = ::recordQueueScroll,
            onQueueRowsApplied = { revision ->
                applyControllerTransition(
                    "queue-rows-applied",
                    controller.workspace.onQueueRowsApplied(revision),
                )
            },
            onSelectKey = ::onSelectKey,
            onRequestUtilityFocus = { target, fallback ->
                applyLatestControllerTransition("utility-focus-request") { latest ->
                    latest.requestFocus(target, fallback)
                }
            },
        )
    }
}
